import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useForm } from 'react-hook-form';

import api from '~/services/api';
import { useStoreActions, useStoreState } from '~/store/hooks';
import Button from '~/ui/components/common/Button';
import NavigationConfirmModal from '~/ui/components/common/NavigationConfirmModal';
import Checkbox from '~/ui/components/inputs/Checkbox';
import Input from '~/ui/components/inputs/Input';
import Select from '~/ui/components/inputs/SelectWithoutAnimation';
import validate, { DEFAULT_VALUES } from './validate';

import { IDictionaryTypes } from '~/services/api/dictionaries/types';
import { IOption } from '~/ui/components/inputs/Select/types';
import Features from '~/ui/constants/features';
import { IInitialValues } from '~/ui/pages/Clinics/ViewClinic/ActTeam/types';
import { extractErrorMessage } from '~/utils/error/error';
import formatDictionaryOptions from '~/utils/formatDictionaryOptions';

import styles from './Form.module.scss';

interface IProps {
  isEdit?: boolean;
  defaultValues?: IInitialValues;
  clinicSelectOptions: (IOption & { hasCrisisTeam: boolean })[];
  isProcessing: boolean;
  leavePageDescription: string;
  cancelLinkTo: any;
  onSubmit: (val: IInitialValues) => void;
}

const Form = ({
  isEdit,
  defaultValues = DEFAULT_VALUES,
  clinicSelectOptions,
  isProcessing,
  cancelLinkTo,
  leavePageDescription,
  onSubmit,
}: IProps): ReactElement => {
  const [typeOfTeamOptions, setTypeOfTeamOptions] = useState<IOption[]>([]);

  const showError = useStoreActions(actions => actions.snackbar.showError);

  const {
    control,
    formState: { errors, isDirty },
    register,
    handleSubmit,
    setValue,
    watch,
  } = useForm({ defaultValues, resolver: validate, mode: 'onBlur' });

  const clinic = useStoreState(state => state.clinic.current);

  const [isCrisisTeam, isShelterTeam, clinicId] = watch([
    'isCrisisTeam',
    'isShelterTeam',
    'clinicId',
  ]);

  const hasCrisisTeam = useMemo(
    () => clinicSelectOptions?.find(option => option.value === clinicId)?.hasCrisisTeam,
    [clinicId, clinicSelectOptions],
  );

  const isCrisisCheckboxDisabled = hasCrisisTeam && !defaultValues.isCrisisTeam;
  const showCrisisCheckbox = clinic.features?.includes(Features.CRISIS);

  const onMount = useCallback(async () => {
    try {
      const { data } = await api.dictionaries.getAvailableTypeList(IDictionaryTypes.TeamType);
      setTypeOfTeamOptions(formatDictionaryOptions(data));
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  }, [showError]);

  useEffect(() => {
    onMount();
  }, [onMount]);

  return (
    <Paper className={styles.paper}>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <Box sx={{ p: 2 }}>
          <h5>Team Details</h5>
          <Grid container spacing={2}>
            <Grid item sm={6}>
              <Select
                options={clinicSelectOptions || []}
                control={control}
                name="clinicId"
                errors={errors}
                label="Clinic"
                isDisabled={isEdit}
                hideSelectedOptions={false}
                openMenuOnFocus
              />
            </Grid>
            <Grid item sm={6} />
            <Grid item sm={6}>
              <Input register={register} errors={errors} name="name" label="Team Name" />
            </Grid>
            <Grid item sm={6} />
            <Grid item sm={6}>
              <Select
                options={typeOfTeamOptions}
                control={control}
                name="typeId"
                errors={errors}
                label="Type of Team"
                hideSelectedOptions={false}
                openMenuOnFocus
              />
            </Grid>
            <Grid item sm={6} />
            <Grid item sm={6}>
              <Input register={register} errors={errors} name="capacity" label="Team Capacity" />
            </Grid>
            <Grid item sm={12}>
              <Checkbox
                size="small"
                name="isShelterTeam"
                control={control}
                errors={errors}
                label="Shelter Team"
                disabled={isCrisisTeam}
                cb={checked => {
                  if (checked) {
                    setValue('isCrisisTeam', false, { shouldValidate: true });
                  }
                }}
              />
            </Grid>
            {showCrisisCheckbox && (
              <Grid item sm={3}>
                {isCrisisCheckboxDisabled ? (
                  <Tooltip
                    title="There is already a team in your clinic designated as a Crisis Team"
                    classes={{ popper: styles.tooltip }}
                  >
                    <div>
                      <Checkbox
                        size="small"
                        name="isCrisisTeam"
                        control={control}
                        errors={errors}
                        label="Crisis Team"
                        disabled
                      />
                    </div>
                  </Tooltip>
                ) : (
                  <Checkbox
                    size="small"
                    name="isCrisisTeam"
                    control={control}
                    errors={errors}
                    label="Crisis Team"
                    disabled={isShelterTeam}
                    cb={checked => {
                      if (checked) {
                        setValue('isShelterTeam', false, { shouldValidate: true });
                      }
                    }}
                  />
                )}
              </Grid>
            )}
          </Grid>
          <div className={styles.buttonsWrapper}>
            <div className={styles.button}>
              <Link to={cancelLinkTo} className={styles.link}>
                <Button type="button" color="primary" variant="outlined">
                  Cancel
                </Button>
              </Link>
            </div>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={isProcessing}
              isLoading={isProcessing}
            >
              Save
            </Button>
          </div>
        </Box>
      </form>
      <NavigationConfirmModal when={isDirty && !isProcessing} description={leavePageDescription} />
    </Paper>
  );
};

export default Form;
